import { useState, useMemo } from 'react';
import { Plus, Pencil, Trash2, Eye, Bell, Power, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { createFileRoute, Link } from '@tanstack/react-router';
import { DeviceModal } from '@/components/devices/device-modal';
import { useDeleteDevicesByIdMutation, useGetDevicesQuery, useGetLocationsQuery } from '@/services';

export const Route = createFileRoute('/_authenticated/(user-dashboard)/dashboard/_dashboard/devices/')({
  component: RouteComponent,
});

// Generate random status for demo purposes
const getRandomStatus = (id: string) => {
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return hash % 4 !== 0; // 75% chance of being online
};

function DeviceCardSkeleton() {
  return (
    <Card className="">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <Skeleton className="h-6 w-32 mb-2" />
            <Skeleton className="h-4 w-24" />
          </div>
          <Skeleton className="h-6 w-16" />
        </div>
      </CardHeader>
      <CardContent className="py-2">
        <div className="flex gap-2 mb-4">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-16" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </CardContent>
      <CardFooter className="pt-2 flex justify-between">
        <Skeleton className="h-8 w-20" />
        <div className="flex gap-2">
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-8" />
        </div>
      </CardFooter>
    </Card>
  );
}

function DevicesGridSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: 8 }).map((_, index) => (
        <DeviceCardSkeleton key={index} />
      ))}
    </div>
  );
}

function RouteComponent() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedDevice, setSelectedDevice] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { data: devices, isLoading, refetch } = useGetDevicesQuery({});
  const { data: locations } = useGetLocationsQuery({});
  const [deleteDevice] = useDeleteDevicesByIdMutation();

  const handleRefresh = async () => {
    try {
      await refetch();
      toast({
        title: 'Success',
        description: 'Devices refreshed successfully',
      });
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to refresh devices',
      });
    }
  };

  const handleEdit = (device: any) => {
    setSelectedDevice(device);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;

    try {
      await deleteDevice({ id: deleteConfirmId }).unwrap();
      toast({
        title: 'Success',
        description: 'Device deleted successfully',
      });
      setDeleteConfirmId(null);
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to delete device',
      });
    }
  };

  const filteredDevices = useMemo(() => {
    if (!devices?.data) return [];

    return devices.data
      .filter((device) => {
        const deviceStatus = getRandomStatus(device.id);
        const matchesSearch =
          device.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          device.serial_number?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesLocation = locationFilter === 'all' || device.location_id === locationFilter;
        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' && deviceStatus) ||
          (statusFilter === 'inactive' && !deviceStatus);

        return matchesSearch && matchesLocation && matchesStatus;
      })
      .map((device) => ({
        ...device,
        status: getRandomStatus(device.id),
        alert_enabled: Math.random() > 0.5,
      }));
  }, [devices?.data, searchTerm, locationFilter, statusFilter]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-24" />
        </div>
        <div className="flex gap-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <DevicesGridSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Devices</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={handleRefresh} title="Refresh devices">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            onClick={() => {
              setSelectedDevice(null);
              setModalMode('create');
              setIsModalOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Device
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Input
          placeholder="Search devices..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
        <Select value={locationFilter} onValueChange={setLocationFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            {locations?.data?.map((location) => (
              <SelectItem key={location.id} value={location.id}>
                {location.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      {(devices?.data?.length ?? 0) > 0 && (
        <div className="text-sm text-muted-foreground">
          Showing {filteredDevices.length} of {devices?.data?.length} devices
        </div>
      )}

      {/* Empty State */}
      {!devices?.data || devices.data.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <div className="flex flex-col items-center space-y-4">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                <Power className="w-8 h-8 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">No devices found</h3>
                <p className="text-muted-foreground max-w-md">
                  Get started by adding your first IoT device to the system. You can monitor sensors, configure alerts,
                  and track device performance.
                </p>
              </div>
              <Button
                onClick={() => {
                  setSelectedDevice(null);
                  setModalMode('create');
                  setIsModalOpen(true);
                }}
                className="mt-4"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Your First Device
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : filteredDevices.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <div className="flex flex-col items-center space-y-4">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                <Power className="w-8 h-8 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">No devices match your filters</h3>
                <p className="text-muted-foreground">
                  Try adjusting your search term or filters to find the devices you're looking for.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setLocationFilter('all');
                  setStatusFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Devices Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3  gap-4">
          {filteredDevices.map((device) => {
            const location = locations?.data?.find((loc) => loc.id === device.location_id);

            return (
              <Card key={device.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg font-semibold truncate">
                        {device.name || 'Unnamed Device'}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">{device.serial_number}</p>
                    </div>
                    <Badge variant={device.status ? 'default' : 'secondary'}>
                      {device.status ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="py-2">
                  <div className="flex gap-2 mb-4">
                    {device.alert_enabled && (
                      <Badge variant="outline" className="text-xs">
                        <Bell className="w-3 h-3 mr-1" />
                        Alerts
                      </Badge>
                    )}
                    {device.status && (
                      <Badge variant="outline" className="text-xs">
                        <Power className="w-3 h-3 mr-1" />
                        Online
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Location:</span>{' '}
                      <span className="text-muted-foreground">{location?.name || 'Unknown'}</span>
                    </div>
                    {device.description && (
                      <div>
                        <span className="font-medium">Description:</span>{' '}
                        <span className="text-muted-foreground">{device.description}</span>
                      </div>
                    )}
                    {device.mac_address && (
                      <div>
                        <span className="font-medium">MAC:</span>{' '}
                        <span className="text-muted-foreground font-mono text-xs">{device.mac_address}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
                <Separator />
                <CardFooter className="pt-4 flex justify-between">
                  <div className="flex items-center space-x-2">
                    <Switch checked={device.status} disabled />
                    <span className="text-xs text-muted-foreground">{device.status ? 'Active' : 'Inactive'}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" asChild>
                      <Link to="/dashboard/devices/$deviceId" params={{ deviceId: device.id }}>
                        <Eye className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(device)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(device.id)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}

      {/* Device Modal */}
      <DeviceModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode={modalMode}
        device={selectedDevice}
        onSuccess={() => {
          setIsModalOpen(false);
          setSelectedDevice(null);
          refetch();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the device and remove all associated data from
              our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
